// src/pages/PaySuccess.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

type PaymentStatus = {
  id: number;
  order_id: string;
  title: string;
  amount: number;
  status: string;
  method: string | null;
  category?: string | null;
  meta?: any;
};

function buildRegistryTarget(meta: any) {
  const m = meta && typeof meta === "object" ? meta : {};

  const categorySlug =
    (m.category_slug ||
      m.categorySlug ||
      m.category ||
      m.contract_type ||
      "rent") + "";

  const sessionKey =
    (m.session_key ||
      m.sessionKey ||
      m.upload_session ||
      m.uploadSession ||
      "") + "";

  const qs = new URLSearchParams();
  qs.set("category", String(categorySlug).toLowerCase());
  if (sessionKey) qs.set("session_key", sessionKey);
  qs.set("registry", "paid");

  return `/contracts/new/transfer?${qs.toString()}`;
}

export default function PaySuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = useMemo(
    () => searchParams.get("orderId") || searchParams.get("orderID") || "",
    [searchParams]
  );

  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState<PaymentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setError("orderId 파라미터가 없습니다.");
      setLoading(false);
      return;
    }

    let alive = true;

    (async () => {
      try {
        const res = await fetch(`/api/payments/status/${encodeURIComponent(orderId)}`, { cache: "no-store" });
        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.ok) {
          throw new Error(json?.message || "결제 상태 조회 실패");
        }

        if (!alive) return;
        setPayment(json.payment as PaymentStatus);
      } catch (e: any) {
        console.error("pay status error:", e);
        if (!alive) return;
        setError(e?.message || "결제 상태 조회 실패");
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (loading) return;
    if (!orderId || error) return;
    if (!payment) return;

    const meta = payment.meta || {};
    const kind = meta.kind || meta.payment_kind || null;

    let target = "/pay";

    const isSuccess = payment.status === "complete" || payment.status === "paid";

    if (isSuccess) {
      if (kind === "registry") {
        target = buildRegistryTarget(meta);
      } else {
        target = "/pay";
      }
    } else {
      target = `/pay/fail?orderId=${encodeURIComponent(orderId)}`;
    }

    const timer = window.setTimeout(() => {
      navigate(target, { replace: true });
    }, 1400);

    return () => window.clearTimeout(timer);
  }, [loading, payment, navigate, orderId, error]);

  if (!orderId) {
    return (
      <div className="min-h-[100dvh] bg-[#f4f8ff] px-4 py-6 sm:px-6 sm:py-10">
        <div className="mx-auto max-w-xl rounded-[32px] border border-[#d8e2f4] bg-white p-6 text-center shadow-[0_20px_50px_rgba(35,76,170,0.12)]">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#5d7bc6]">Payment Status</div>
          <h1 className="mt-2 text-[28px] font-black tracking-[-0.03em] text-slate-950">결제 정보가 없습니다</h1>
          <p className="mt-3 text-sm leading-6 text-slate-600">주문 번호가 전달되지 않아 상태를 확인할 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate("/pay", { replace: true })}
            className="mt-6 inline-flex w-full items-center justify-center rounded-[18px] bg-[#2f63e1] px-6 py-3 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(47,99,225,0.25)] transition hover:bg-[#2859d1]"
          >
            결제하기로 이동
          </button>
        </div>
      </div>
    );
  }

  const isSuccess = payment && (payment.status === "complete" || payment.status === "paid");

  return (
    <div className="min-h-[100dvh] bg-[#f4f8ff] px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-xl">
        <div className="mb-5 rounded-[30px] border border-[#dbe6fb] bg-white/95 p-5 shadow-[0_20px_45px_rgba(61,107,219,0.12)] sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#5d7bc6]">Payment Status</div>
              <h1 className="mt-2 text-[28px] font-black tracking-[-0.03em] text-slate-950 sm:text-[32px]">결제 결과 확인</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">결제 상태를 확인한 뒤 다음 화면으로 자동 이동합니다.</p>
            </div>
            <div className="rounded-2xl bg-[#eef4ff] px-3 py-2 text-right">
              <div className="text-[11px] font-bold text-[#5d7bc6]">상태</div>
              <div className="text-lg font-black text-[#2f63e1]">{loading ? "확인중" : error ? "오류" : isSuccess ? "성공" : "확인"}</div>
            </div>
          </div>
        </div>

        <div className="rounded-[32px] border border-[#d8e2f4] bg-white p-5 shadow-[0_20px_50px_rgba(35,76,170,0.12)] sm:p-6">
          {loading ? (
            <>
              <div className="rounded-[24px] bg-[#091122] px-4 py-5 text-white">
                <div className="text-lg font-black">결제 결과 확인 중</div>
                <div className="mt-2 text-sm text-white/70">잠시만 기다려 주세요. 승인 결과를 조회하고 있습니다.</div>
              </div>
              <div className="mt-4 rounded-[24px] border border-[#e7eefb] bg-[#fbfdff] p-4 text-sm text-slate-600">
                주문번호를 기준으로 상태를 확인한 뒤 자동으로 다음 화면으로 이동합니다.
              </div>
            </>
          ) : null}

          {!loading && error ? (
            <>
              <div className="rounded-[22px] border border-rose-200 bg-rose-50 px-4 py-4 text-sm font-semibold text-rose-700 whitespace-pre-line">{error}</div>
              <p className="mt-3 text-sm leading-6 text-slate-600">아래 버튼으로 결제하기 화면으로 돌아가 다시 진행할 수 있습니다.</p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={() => navigate("/mypage", { replace: true })}
                  className="inline-flex items-center justify-center rounded-[18px] border border-[#d1dbed] bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-[#f7faff]"
                >
                  내 결제내역 보기
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/pay", { replace: true })}
                  className="inline-flex items-center justify-center rounded-[18px] bg-[#2f63e1] px-6 py-3 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(47,99,225,0.25)] transition hover:bg-[#2859d1]"
                >
                  결제하기로 이동
                </button>
              </div>
            </>
          ) : null}

          {!loading && !error && payment ? (
            <>
              <div className={["rounded-[24px] px-4 py-5 text-white", isSuccess ? "bg-[#0a1328]" : "bg-[#5b6476]"].join(" ")}>
                <div className="text-lg font-black">{isSuccess ? "결제가 확인되었습니다" : "결제 상태를 다시 확인해 주세요"}</div>
                <div className="mt-2 text-sm text-white/70">{isSuccess ? "곧 다음 단계로 자동 이동합니다." : "정상 결제가 아니라면 실패 화면으로 이동합니다."}</div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="rounded-[22px] border border-[#edf2fb] bg-[#fbfdff] p-4">
                  <div className="text-xs font-extrabold text-slate-500">주문번호</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900 break-all">{payment.order_id}</div>
                </div>
                <div className="rounded-[22px] border border-[#edf2fb] bg-[#fbfdff] p-4">
                  <div className="text-xs font-extrabold text-slate-500">결제금액</div>
                  <div className="mt-2 text-lg font-black text-slate-950">{Number(payment.amount || 0).toLocaleString("ko-KR")}원</div>
                </div>
                <div className="rounded-[22px] border border-[#edf2fb] bg-[#fbfdff] p-4">
                  <div className="text-xs font-extrabold text-slate-500">결제명</div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{payment.title || "결제 건"}</div>
                </div>
              </div>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  onClick={() => navigate("/mypage", { replace: true })}
                  className="inline-flex items-center justify-center rounded-[18px] border border-[#d1dbed] bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-[#f7faff]"
                >
                  내 결제내역 보기
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/pay", { replace: true })}
                  className="inline-flex items-center justify-center rounded-[18px] bg-[#2f63e1] px-6 py-3 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(47,99,225,0.25)] transition hover:bg-[#2859d1]"
                >
                  결제하기로 이동
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
